import { lazy, ReactNode } from "react";
import { ERole } from "../../shared/enums/user/ERole";
import {
  CalendarIcon,
  CustomersIcon,
  DashboardIcon,
  OffersIcon,
  UsersIcon,
  VisitsIcon,
} from "../../shared/components/Icons";
import { AppRoute, PublicRoute, SidebarRoute } from "./types";

const Dashboard = lazy(() => import("../../app/dashboard"));
const CustomersPage = lazy(() => import("../../app/customers"));
const CustomerPreview = lazy(
  () => import("../../app/customers/components/CustomerPreview"),
);
const CalendarPage = lazy(() => import("../../app/calendar"));
const VisitsPage = lazy(() => import("../../app/visits"));
const OffersPage = lazy(() => import("../../app/offers"));
const OfferPreview = lazy(
  () => import("../../app/offers/components/OfferPreview"),
);
const OfferResponse = lazy(
  () => import("../../app/offers/components/OfferResponse"),
);
const MaterialList = lazy(
  () => import("../../app/offers/components/MaterialList"),
);
const UsersPage = lazy(() => import("../../app/user"));
const UserCreate = lazy(() => import("../../app/user/components/UserCreate"));
const UserUpdate = lazy(() => import("../../app/user/components/UserUpdate"));
const UserCalendar = lazy(
  () => import("../../app/user/components/UserCalendar"),
);
const UserProfilePage = lazy(() => import("../../app/profile"));
const SettingsPage = lazy(() => import("../../app/settings"));
const LocationsPage = lazy(() => import("../../app/locations"));
const FloorsPage = lazy(() => import("../../app/floors"));
const FloorPlanEditor = lazy(
  () => import("../../app/floors/components/FloorPlanEditor"),
);
const FlatsPage = lazy(() => import("../../app/flats"));
const FlatDetail = lazy(() => import("../../app/flats/components/FlatDetail"));
const FlatCanvas = lazy(() => import("../../app/flats/components/FlatCanvas"));
const Notifications = lazy(() => import("../../app/notifications"));

const Login = lazy(() => import("../../authentification/login"));
const Signup = lazy(() => import("../../authentification/signup"));
const CustomerRegisterStepTwo = lazy(
  () => import("../../authentification/signup/components/CustomerRegisterStepTwo"),
);
const ForgotPasswordRequest = lazy(
  () => import("../../authentification/forgotPassword/request"),
);
const ForgotPasswordResponse = lazy(
  () => import("../../authentification/forgotPassword/response"),
);

const STAFF = [ERole.ROLE_ADMIN, ERole.ROLE_COMPANY, ERole.ROLE_PROJECT_MANAGER];
const WORKER = [ERole.ROLE_WORKER];
const CLIENT = [
  ERole.ROLE_CUSTOMER,
  ERole.ROLE_BUSINESS_PARTNER,
  ERole.ROLE_USER,
];
const EVERYONE = [...STAFF, ...WORKER, ...CLIENT];

/**
 * Flats hang off a floor, but are also reachable straight from a location.
 * Both URL shapes render the same page, so each page is declared once here
 * and expanded into the two paths.
 */
const flatRoutes = (
  suffix: string,
  element: ReactNode,
  roles: ERole[],
): AppRoute[] =>
  [
    `/customers/:customerId/locations/:locationId/floors/:floorId${suffix}`,
    `/customers/:customerId/locations/:locationId${suffix}`,
  ].map((path) => ({ path, element, roles }));

export const appRoutes: AppRoute[] = [
  {
    path: "/dashboard",
    element: <Dashboard />,
    roles: EVERYONE,
    sidebar: {
      title: "Dashboard",
      icon: (iconColor) => <DashboardIcon fill={iconColor} />,
    },
  },
  {
    path: "/customers",
    element: <CustomersPage />,
    roles: EVERYONE,
    sidebar: {
      title: "Customers",
      icon: (iconColor) => <CustomersIcon fill={iconColor} />,
    },
  },
  {
    path: "/customer-preview/:id",
    element: <CustomerPreview />,
    roles: EVERYONE,
  },
  {
    path: "/calendar-global",
    element: <CalendarPage />,
    roles: EVERYONE,
    sidebar: {
      title: "Calendar",
      icon: (iconColor) => <CalendarIcon fill={iconColor} />,
    },
  },
  {
    /* Customer visits: planning, reports and follow-ups. The web API behind it
       is limited to admins and project managers. */
    path: "/visits",
    element: <VisitsPage />,
    roles: [ERole.ROLE_ADMIN, ERole.ROLE_PROJECT_MANAGER],
    sidebar: {
      title: "Visits",
      icon: (iconColor) => <VisitsIcon fill={iconColor} />,
    },
  },
  {
    path: "/offers",
    element: <OffersPage />,
    roles: [...STAFF, ...CLIENT],
    sidebar: {
      title: "Offers",
      icon: (iconColor) => <OffersIcon fill={iconColor} />,
    },
  },
  {
    path: "/offers/preview/:offerId",
    element: <OfferPreview />,
    roles: [...STAFF, ...CLIENT],
  },
  {
    path: "/offers/material-list/:offerId",
    element: <MaterialList />,
    roles: STAFF,
  },
  {
    path: "/offer/status",
    element: <OfferResponse />,
    roles: [...STAFF, ...CLIENT],
  },
  {
    path: "/users",
    element: <UsersPage />,
    roles: STAFF,
    sidebar: {
      title: "Users",
      icon: (iconColor) => <UsersIcon fill={iconColor} />,
    },
  },
  { path: "/users/create", element: <UserCreate />, roles: STAFF },
  { path: "/users/:userId", element: <UserUpdate />, roles: STAFF },
  { path: "/users/:userId/calendar", element: <UserCalendar />, roles: STAFF },
  { path: "/settings", element: <SettingsPage />, roles: STAFF },
  { path: "/profile", element: <UserProfilePage />, roles: EVERYONE },
  { path: "/notifications", element: <Notifications />, roles: EVERYONE },
  {
    path: "/customers/:customerId/locations",
    element: <LocationsPage />,
    roles: EVERYONE,
  },
  {
    path: "/customers/:customerId/locations/:locationId/floors",
    element: <FloorsPage />,
    roles: EVERYONE,
  },
  {
    path: "/customers/:customerId/locations/:locationId/floors/:floorId/plan-editor",
    element: <FloorPlanEditor />,
    roles: EVERYONE,
  },
  ...flatRoutes("/flats", <FlatsPage />, EVERYONE),
  ...flatRoutes("/flats/:flatId", <FlatDetail />, EVERYONE),
  ...flatRoutes("/flats/:flatId/canvas", <FlatCanvas />, STAFF),
  // The flat plan editor was folded into the flat canvas; its old URLs still
  // serve, so links and bookmarks to a flat's plan keep working.
  ...flatRoutes("/flats/:flatId/plan-editor", <FlatCanvas />, STAFF),
];

export const publicRoutes: PublicRoute[] = [
  { path: "/", element: <Login /> },
  { path: "/register", element: <Signup /> },
  { path: "/customer-register", element: <CustomerRegisterStepTwo /> },
  { path: "/offer/status", element: <OfferResponse /> },
  { path: "/forgot-password", element: <ForgotPasswordRequest /> },
  { path: "/forgot-password-response", element: <ForgotPasswordResponse /> },
];

const hasSidebarEntry = (route: AppRoute): route is SidebarRoute =>
  !!route.sidebar;

/** Routes the given roles are allowed to reach. */
export const getRoutesForRoles = (roles: string[] | undefined): AppRoute[] =>
  appRoutes.filter((route) =>
    route.roles.some((allowed) => roles?.includes(allowed)),
  );

/** Sidebar links for the given roles, in registry order. */
export const getSidebarRoutes = (
  roles: string[] | undefined,
): SidebarRoute[] => getRoutesForRoles(roles).filter(hasSidebarEntry);
